import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { MonarchDataConsumer } from "@/monarch/MonarchDataConsumer";
import { ExecEdDetailPageTestResult } from "@/monarch/rules/execEdDetailPageTest/types";
import { useEffect } from "react";
import { useInView } from "react-intersection-observer";
import getCountdownTime from "@/utils/functions/getCountdownTime";

interface CountdownBannerProps {
  position: string;
  startDate: Date | null | undefined;
  className?: string;
}

const CountdownBanner = ({
  position,
  startDate,
  className,
}: CountdownBannerProps) => {
  const { elementClicked, elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      text: "EE-CountdownBanner",
      name: "EE-CountdownBanner",
    },
  });
  const { ref: bannerRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  useEffect(() => {
    if (inView) {
      elementViewed();
    }
  }, [inView, elementViewed]);

  return (
    <MonarchDataConsumer<ExecEdDetailPageTestResult>
      experimentKey="useExecEdDetailPageTest"
      render={({ data }) => {
        if (data?.countdownBanner.position !== position || !startDate) {
          return <></>;
        }
        const start =
          typeof startDate === "string" ? new Date(startDate) : startDate;

        const { text, theme } = data?.countdownBanner;
        const countdownTime = getCountdownTime(start);

        if (!countdownTime) {
          return <></>;
        }

        const { days, hours, minutes } = countdownTime;

        if (days > 14) {
          return <></>;
        }

        return (
          <div
            ref={bannerRef}
            className={`${theme === "light" ? "bg-putty-300" : "bg-secondary text-white"} rounded py-2 px-2 mb-8 md:mb-0 flex flex-col lg:flex-row items-center justify-center ${className}`}
          >
            <span className="font-semibold mr-4">{text}</span>
            <div className="flex flex-row items-center">
              <span className="text-xl font-bold">{days}</span>
              <span className="text-sm ml-1 md:ml-2">
                {days === 1 ? "Day" : "Days"}
              </span>
              <span className="text-xl font-bold mx-1 md:mx-2">:</span>
              <span className="text-xl font-bold">{hours}</span>
              <span className="text-sm ml-1 md:ml-2">
                {hours === 1 ? "Hour" : "Hours"}
              </span>
              <span className="text-xl font-bold mx-1 md:mx-2">:</span>
              <span className="text-xl font-bold">{minutes}</span>
              <span className="text-sm ml-1 md:ml-2">
                {minutes === 1 ? "Minute" : "Minutes"}
              </span>
            </div>
          </div>
        );
      }}
    />
  );
};

export default CountdownBanner;
